// V1 source: customer/midas/components/DiagramHotspotRect.tsx
// Strategy: logic-copied
// Verified no-MUI: yes
//
// V2 hotspot SVG group — Tier A behavioral parity with V1. Logic copied
// verbatim: color/bg/label utils, opacity dimming when other hotspots are
// highlighted, pulse animation on the active step, label-pill positioning.
//
// V1 had no MUI to scrub here — pure SVG. V2 was byte-equivalent except for
// the file name + the V2 file-header convention.
//
// 2026-08-21 (DIAG-OVERLAY-A): V2 now also prefers the OPTIONAL server-served
// `hotspot.color` over the local type -> color map. V1 is legacy and stays on
// the map alone, so this is the one intentional divergence from V1.
//
// 2026-08-21 (DIAG-OVERLAY-B): V2 also renders an OPTIONAL transient flash ring
// (`flashMode`) driven by a <comp-ref> chip click. Distinct from `isActive`:
// isActive is the step-navigator's persistent "current step" pulse (indefinite
// repeat), flashMode is a one-shot ~1.6s acknowledgement that clears itself.
// Both can be on the same hotspot at once and compose fine (separate rects).

import React, { useCallback, useMemo } from "react";
import { DiagramHotspot } from "../../../../shared/chatBot/models/chatBotModels";
import {
  getDiagramColor,
  getDiagramBgColor,
  getDiagramLabelColors,
} from "../../utils/diagramColorMap";

interface Props {
  hotspot: DiagramHotspot;
  isHighlighted: boolean;
  isActive: boolean;
  /** true when at least one hotspot in the set is highlighted */
  hasAnyHighlighted: boolean;
  /**
   * DIAG-OVERLAY-B: transient comp-ref flash for THIS hotspot.
   *   undefined → not flashing (the only state any pre-existing caller sees).
   *   "pulse"   → animated stroke-opacity ring (default).
   *   "static"  → non-animated ring, for `prefers-reduced-motion: reduce`.
   * The static variant is a real requirement, not a degradation: SVG SMIL is
   * outside the reach of the CSS reduced-motion override, so a reduced-motion
   * user would otherwise see the full animation.
   */
  flashMode?: "pulse" | "static";
  onMouseEnter: (id: string, e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseLeave: () => void;
  onClick: (id: string) => void;
}

const DiagramHotspotRectV2: React.FC<Props> = ({
  hotspot,
  isHighlighted,
  isActive,
  hasAnyHighlighted,
  flashMode,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
  onClick,
}) => {
  // Served color (bot canonical palette, optional) wins; our type -> color
  // map is the fallback when the bot omits it. See diagramColorMap.ts.
  const color = getDiagramColor(hotspot.type, hotspot.color);
  const bgColor = getDiagramBgColor(hotspot.type, hotspot.color);
  const labelColors = useMemo(
    () => getDiagramLabelColors(hotspot.type, hotspot.color),
    [hotspot.type, hotspot.color]
  );

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent) => onMouseEnter(hotspot.vi_id, e),
    [hotspot.vi_id, onMouseEnter]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClick(hotspot.vi_id);
    },
    [hotspot.vi_id, onClick]
  );

  // Determine opacity: highlighted = full, non-highlighted-when-some-highlighted = dimmed
  //
  // DIAG-OVERLAY-B: a flashing hotspot is forced to FULL opacity. Without this
  // override, clicking a comp-ref chip for a component that is not part of the
  // answer's highlighted set would paint the flash at 0.35 alpha on a busy
  // schematic — i.e. the user clicks and sees nothing. The override lasts only
  // as long as the flash.
  const groupOpacity = flashMode
    ? 1
    : hasAnyHighlighted && !isHighlighted
    ? 0.35
    : 1;

  // Stroke and fill based on highlight state
  const strokeWidth = isHighlighted ? 0.4 : 0.25;
  const fillColor = isHighlighted ? (color + '40') : bgColor; // 25% vs 12%
  const filter = isHighlighted ? `drop-shadow(0 0 0.5px ${color})` : undefined;

  // Label positioning: pill just above the rect
  const labelX = hotspot.x_pct;
  const labelY = hotspot.y_pct - 0.3; // slightly above top edge
  const labelText = hotspot.label || hotspot.component_id || '';

  // Approximate label pill width (percentage-based, ~0.55% per char, min 2.5%)
  const pillWidth = Math.max(labelText.length * 0.55, 2.5);
  const pillHeight = 1.6;

  return (
    <g
      onMouseEnter={handleMouseEnter}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onClick={handleClick}
      style={{ cursor: "pointer", opacity: groupOpacity }}
      filter={filter}
    >
      {/* Main rectangle */}
      <rect
        x={`${hotspot.x_pct}%`}
        y={`${hotspot.y_pct}%`}
        width={`${hotspot.w_pct}%`}
        height={`${hotspot.h_pct}%`}
        fill={fillColor}
        stroke={color}
        strokeWidth={strokeWidth}
        rx="0"
        ry="0"
      />

      {/* Pulse animation for active step */}
      {isActive && (
        <rect
          x={`${hotspot.x_pct}%`}
          y={`${hotspot.y_pct}%`}
          width={`${hotspot.w_pct}%`}
          height={`${hotspot.h_pct}%`}
          fill="none"
          stroke={color}
          strokeWidth={0.6}
          rx="0"
          ry="0"
        >
          <animate
            attributeName="stroke-opacity"
            values="0.3;0.8;0.3"
            dur="1.5s"
            repeatCount="indefinite"
          />
        </rect>
      )}

      {/* DIAG-OVERLAY-B — transient comp-ref flash ring.
          Drawn OUTSIDE the hotspot box (inflated by ~0.35% on each side) so it
          reads as an acknowledgement around the component rather than as a
          second highlight state of it, and so it stays legible on top of the
          isActive pulse when both are on.
          repeatCount is FINITE (3 cycles ≈ 1.6s, matching
          COMP_FLASH_DURATION_MS) — the viewer unmounts this rect when its
          timer fires, but a finite count means the ring can never survive as
          an infinite animation if a future caller forgets to clear it. */}
      {flashMode && (
        <rect
          x={`${hotspot.x_pct - 0.35}%`}
          y={`${hotspot.y_pct - 0.35}%`}
          width={`${hotspot.w_pct + 0.7}%`}
          height={`${hotspot.h_pct + 0.7}%`}
          fill="none"
          stroke={color}
          strokeWidth={0.85}
          strokeOpacity={flashMode === "static" ? 1 : undefined}
          rx="0.2"
          ry="0.2"
        >
          {flashMode === "pulse" && (
            <animate
              attributeName="stroke-opacity"
              values="1;0.2;1"
              dur="0.533s"
              repeatCount="3"
              fill="freeze"
            />
          )}
        </rect>
      )}

      {/* Label pill background */}
      {labelText && (
        <rect
          x={`${labelX}%`}
          y={`${labelY - pillHeight}%`}
          width={`${pillWidth}%`}
          height={`${pillHeight}%`}
          fill={labelColors.bg}
          rx="0.3"
          ry="0.3"
        />
      )}

      {/* Label text */}
      {labelText && (
        <text
          x={`${labelX + pillWidth / 2}%`}
          y={`${labelY - pillHeight / 2}%`}
          textAnchor="middle"
          dominantBaseline="central"
          fill={labelColors.fg}
          fontSize="0.9"
          fontFamily="'JetBrains Mono', 'Consolas', monospace"
          fontWeight="700"
        >
          {labelText}
        </text>
      )}
    </g>
  );
};

export default React.memo(DiagramHotspotRectV2);
